import React from "react";
import {
  RELOAD_MARK_KEY,
  isChunkLoadError,
  reloadMark,
  shouldAutoReload,
} from "./chunkError";

/**
 * The app had no error boundary at all. React 18 unmounts the entire tree when
 * a render throws with nothing to catch it, so every failure — a stale chunk
 * after a deploy, a null dereference in one panel — produced the same result:
 * an empty `#root` and a blank navy page with no way back but a hard refresh.
 *
 * This catches both, and treats them differently:
 *
 *   - a stale chunk reloads itself, once, because a reload genuinely fixes it;
 *   - anything else shows what broke and offers the reload as a button, because
 *     reloading a component crash just crashes it again.
 *
 * See chunkError.ts for why the reload is guarded rather than unconditional.
 */

/** sessionStorage is unavailable in some privacy modes; never let that throw. */
function readMark(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_MARK_KEY);
  } catch {
    // Unreadable is not "absent" — refuse the automatic reload and let the
    // user press the button, rather than risk a loop we cannot see.
    return "";
  }
}

function writeMark(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARK_KEY, reloadMark(now));
  } catch {
    /* best effort — the cooldown is a safety net, not a correctness rule */
  }
}

/**
 * A reload that cannot be answered from a cache. A plain `location.reload()`
 * can hand back an index.html that still names last deploy's chunks (a browser
 * or proxy cache, or a CDN edge mid-rollout), and then every Reload press fails
 * the same way (Mikko, 2026-10-19: pressed Reload several times, "still not
 * resolved"). A new query string is a new URL for every cache in the path, and
 * Cache Storage is emptied first in case anything was kept there. main.tsx
 * strips the marker again once the page has booted.
 */
export function hardReload(): void {
  const go = (): void => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("_r", String(Date.now()));
      window.location.replace(u.toString());
    } catch {
      window.location.reload();
    }
  };
  try {
    if ("caches" in window) {
      void window.caches.keys().then((ks) => Promise.all(ks.map((k) => window.caches.delete(k)))).then(go, go);
      return;
    }
  } catch { /* fall through */ }
  go();
}

/**
 * Reload if this looks like a chunk the CDN no longer has and we have not
 * already tried that. Exported because main.tsx wires the same decision to
 * Vite's `vite:preloadError`, which fires before React ever renders.
 */
export function recoverFromChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;

  const now = Date.now();
  if (!shouldAutoReload(readMark(), now)) return false;

  writeMark(now);
  hardReload();
  return true;
}

interface IState {
  error: Error | null;
  reloading: boolean;
}

const wrap: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "#081326",
  color: "#e6edf7",
  font: "400 14px/1.6 Inter, system-ui, sans-serif",
};

const card: React.CSSProperties = {
  maxWidth: 520,
  width: "100%",
  padding: "28px 30px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
};

const button: React.CSSProperties = {
  marginTop: 22,
  padding: "10px 22px",
  borderRadius: 9,
  border: "none",
  cursor: "pointer",
  font: "600 14px/1 Inter, system-ui, sans-serif",
  color: "#08111f",
  background: "linear-gradient(180deg,#d8b45f,#c19a3f)",
};

export class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  IState
> {
  state: IState = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): IState {
    return { error, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error): void {
    // Reloading unmounts everything a moment from now, so keep the spinner
    // copy up rather than flashing the error card on the way out.
    if (recoverFromChunkError(error)) return;
    if (this.state.reloading) this.setState({ reloading: false });
    console.error("[i-SYNERGY] unhandled render error", error);
  }

  private readonly reload = () => {
    hardReload();
  };

  render(): React.ReactNode {
    const { error, reloading } = this.state;
    if (error === null) return this.props.children;

    if (reloading) {
      return (
        <div style={wrap}>
          <div style={{ ...card, textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Updating to the latest version…</div>
            <div style={{ marginTop: 8, opacity: 0.7 }}>This page will reload on its own.</div>
          </div>
        </div>
      );
    }

    return (
      <div style={wrap}>
        <div style={card} role="alert">
          <div style={{ fontSize: 17, fontWeight: 700 }}>This screen could not load</div>
          <div style={{ marginTop: 10, opacity: 0.8 }}>
            Something failed while rendering. Your data is untouched — reloading
            is safe.
          </div>
          <div
            style={{
              marginTop: 16,
              padding: "10px 12px",
              borderRadius: 8,
              background: "rgba(0,0,0,0.28)",
              font: "400 12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace",
              color: "#f2b8b8",
              wordBreak: "break-word",
            }}
          >
            {error.message || String(error)}
          </div>
          <button type="button" style={button} onClick={this.reload}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
